import { ChangeDetectionStrategy, Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatTable } from '@angular/material/table';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { BehaviorSubject, EMPTY, Observable, of, switchMap } from 'rxjs';

import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { AccountColor, FieldDef, FieldDefType } from 'app/core/models';
import { Dictionary } from 'app/shared/models';
import { MatrixPanelMeta, PanelComponentBase } from '../panels.types';
import { FormatNumberPipe } from 'app/shared/pipes/format-number.pipe';
import { RemoveZeroDecimalsPipe } from 'app/shared/pipes/remove-zero-decimals.pipe';
import { FeatureFlag, NewPermission } from '@core/enums';
import { RelatedToType } from '@shared/enums';
import { ColumnDef, DEFAULT_GRID_OPTIONS } from 'tsui/lib/ts-table/ts-table.model';
import {
  RedirectItem,
  TsButtonModule,
  TsColorBubbleComponent,
  TSIconModule,
  TsOverlayModule,
  TsTableService,
} from 'tsui';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Project } from 'app/modules/projects/projects.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { FieldDefsState } from '@state/fields/state';
import { GridApi, GridOptions, GridReadyEvent } from 'ag-grid-enterprise';
import { TableFooterAggRendererComponent } from '@shared/components/ts-table-renderers/table-footer-agg-renderer-tables-panel/table-footer-agg-renderer-tables-panel.component';
import {
  MatrixExportDialogComponent,
  MatrixExportDialogResult,
} from './matrix-export-dialog/matrix-export-dialog.component';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { UserState } from '@state/user/state';
import { MatLegacyTableModule } from '@angular/material/legacy-table';
import { TsTableComponent } from 'tsui/lib/ts-table';
import { DirectivesModule } from '@shared/directives/directives.module';
import { AsyncPipe, KeyValuePipe, NgClass, NgFor, NgIf, TitleCasePipe } from '@angular/common';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { PipesModule } from '@shared/pipes/pipes.module';
import { FormFieldsModule } from '@shared/components/form-fields/form-fields.module';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { AccountColorBackgroundDirective } from '@shared/directives/account-color-background.directive';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { EditableModule } from '@shared/components/editable/editable.module';
import { WidgetsModule } from '@shared/components/widgets/widgets.module';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { Router } from '@angular/router';

type ManageMode = 'add' | 'remove';
type SumOrAvg = 'sum' | 'avg';

@Component({
  selector: 'app-matrix',
  templateUrl: './matrix.component.html',
  styleUrls: ['./matrix.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatLegacyTableModule,
    TsTableComponent,
    TSIconModule,
    DragDropModule,
    AccountColorBackgroundDirective,
    NgClass,
    NgIf,
    NgFor,
    TsButtonModule,
    MatLegacyFormFieldModule,
    PipesModule,
    FormFieldsModule,
    MatLegacyTooltipModule,
    ReactiveFormsModule,
    MatLegacyMenuModule,
    DirectivesModule,
    TsColorBubbleComponent,
    AsyncPipe,
    EditableModule,
    WidgetsModule,
    TsOverlayModule,
    TitleCasePipe,
    KeyValuePipe,
    MatLegacyInputModule,
    MatLegacyButtonModule,
  ],
})
export class PanelMatrixComponent extends PanelComponentBase<MatrixPanelMeta> implements OnInit {
  @ViewChild('table') table!: MatTable<any>;

  title = new FormControl('');
  columnControls: Dictionary<FormControl> = {};
  rowControls: Dictionary<FormControl> = {};
  isFullWidth = signal(false);
  panelColor: any = null;
  columnDefs: ColumnDef[] = [];
  rowData: any[] = [];
  newTable = signal(false);
  gridOptions: GridOptions;
  relatedToRedirectConfig: Dictionary<RedirectItem> = {
    Contact: { route: (): Observable<string> => of('/contacts/:id') },
    Company: { route: (): Observable<string> => of('/companies/:id') },
    Property: { route: (): Observable<string> => of('/properties/:id') },
    Project: {
      deferred: true,
      route: (key?: number): Observable<string> => {
        return this._projectsService.getProjectByIdBase(String(key)).pipe(
          untilComponentDestroyed(this),
          switchMap((project: Project) => of(`/projects/${project.pipeline_id}/${key}`)),
        );
      },
    } as RedirectItem,
  };
  layoutFieldDefs = new BehaviorSubject([]);
  savingChanges = signal(false);
  pendingChanges = signal({});

  readonly relatedToTypeEnum = RelatedToType;

  private gridApi!: GridApi<any>;
  private placeholderText = '[placeholder]';
  private aggValidTypes = ['number', 'money', 'decimal', 'percent'];
  private placeholderField: FieldDef = { type: 'text', name: this.placeholderText, label: 'No field selected' };
  private modelMemento: any;

  protected store = inject(Store);

  protected readonly accountColors$: Observable<AccountColor[]> = inject(Store).select(
    AccountState.selectAccountColorsWithNone,
  );

  get isEmpty(): boolean {
    return !this.panelDefColumnsLength && !this.panelDefFieldsLength;
  }

  get panelDefColumns(): string[] {
    return this.panelDef.meta?.columns;
  }

  get panelDefColumnsLength(): number {
    return this.panelDefColumns?.length;
  }

  get panelDefFields(): string[][] {
    return this.panelDef.meta?.fields;
  }

  get panelDefFieldsLength(): number {
    return this.panelDefFields?.length;
  }

  constructor(
    private tsTableService: TsTableService,
    private _projectsService: ProjectsService,
    private dialog: MatLegacyDialog,
    private snackBar: MatSnackBar,
    private _propertiesService: PropertiesService,
    private readonly router: Router,
  ) {
    super();

    this.relatedToRedirectConfig = this.store
      .selectSnapshot(ObjectsManagerState.get)
      .filter((x) => x.id)
      .reduce((p, c) => {
        return { ...p, [c.name]: { route: (): Observable<string> => of(`/objects/${c.id}/:id/details`) } };
      }, this.relatedToRedirectConfig);
  }

  ngOnInit(): void {
    this.checkTable();
    this.setGridOptions();
    this.initializePanelDefMeta();
    this.ui = { ...this.ui, editMode: false };
    this.panelDefMemento = JSON.parse(JSON.stringify(this.panelDef));
    this.modelMemento = JSON.parse(JSON.stringify(this.model));
    this.fieldDefs = [this.placeholderField, ...this.fieldDefs];
    this.updateColumnControls();
    this.updateRowControls();
    this.setDynamicColDefs();
    this.setRowData();
    this.setGridOptions();
    this.setLayoutFields();
  }

  private initializePanelDefMeta(): void {
    // Initialize meta if it doesn't exist
    if (!this.panelDef.meta) {
      this.panelDef.meta = {
        columns: [],
        fields: [],
        footer: [],
        showFooter: true,
      };
      return;
    }

    // Initialize individual properties if they don't exist
    if (!this.panelDefColumns) {
      this.panelDef.meta.columns = [];
    }

    if (!this.panelDefFields) {
      this.panelDef.meta.fields = [];
    }

    if (!this.panelDef.meta.footer) {
      this.panelDef.meta.footer =
        this.panelDefColumns?.map((column, index) => (index === 0 ? '[sum-avg]' : 'sum')) || [];
    }

    if (this.panelDef.meta.showFooter === undefined) {
      this.panelDef.meta.showFooter = true;
    }
  }

  toggleEditMode(): void {
    this.ui = { ...this.ui, editMode: !this.ui.editMode };
    this.title.setValue(this.panelDef.title);
    this.editToggled.emit({ toggle: true, panelDef: this.panelDef });

    if (!this.panelDefColumnsLength) {
      this.columnControls[0] = new FormControl('[column]');
      this.columnControls[1] = new FormControl('Column1');
      this.rowControls[0] = new FormControl('Row1');

      this.panelDef = {
        ...this.panelDef,
        meta: {
          columns: ['[column]', 'Column1'],
          fields: [['Row1', this.placeholderField.name]],
          footer: ['[sum-avg]', 'sum'],
        },
      };
    }
  }

  selectHeaderColor(color: string): void {
    if (color?.toLowerCase() === 'none') {
      this.panelColor = null;
    } else {
      this.panelColor = color;
    }

    this.saveHeaderColor();
  }

  toggleCellsWidth(): void {
    this.isFullWidth.set(!this.isFullWidth());
  }

  cancelEdits(): void {
    this.ui = { ...this.ui, editMode: !this.ui.editMode };
    this.panelDef = JSON.parse(JSON.stringify(this.panelDefMemento));
    this.updateColumnControls();
    this.updateRowControls();
    this.editToggled.emit({ toggle: false, panelDef: this.panelDef });
  }

  private setGridOptions(): void {
    this.gridOptions = {
      ...DEFAULT_GRID_OPTIONS,
      context: {
        suppressAggFuncInHeader: true,
        panelDef: this.panelDef,
        toggleAgg: (columnId: string, type: SumOrAvg) => {
          const columnIndex = this.columnDefs.findIndex((col) => col.name === columnId);
          if (columnIndex >= 0) {
            this.toggleAgg(columnIndex, type);
          }
        },
        groupIncludeFooter: (params) => {
          return params?.node?.rowIndex !== 0;
        },
        getRowStyle: (params) => {
          if (params?.node?.rowPinned) {
            return { fontWeight: 'bold', background: '#f4f4f4' };
          }
          return null;
        },
      },
    };
  }

  private saveHeaderColor(): void {
    this.panelDef = {
      ...this.panelDef,
      color: this.panelColor,
    };

    this.panelDefUpdated.emit(this.panelDef);
  }

  private updateRowControls(): void {
    this.panelDefFields.forEach((row, index) => {
      const rowLabel = row[0];
      this.rowControls[index] = new FormControl(rowLabel);
    });
  }

  private updateColumnControls(): void {
    this.panelDefColumns?.forEach((column, index) => {
      this.columnControls[index] = new FormControl(column);
    });
  }

  private getField(params) {
    const columnIndex = params.colDef.field.split('_')[1];
    const rowIndex = params.data?.rowIndex;
    const fieldName = this.panelDefFields[rowIndex]?.[columnIndex];
    const fieldDef = this.lookupField(fieldName, true);
    return fieldDef;
  }

  dropColumns(event: CdkDragDrop<string[]>): void {
    if (!this.isColumnDraggable(event.previousIndex) || !this.isColumnDraggable(event.currentIndex)) {
      return;
    }

    if (event.previousIndex !== event.currentIndex) {
      const columns = [...this.panelDefColumns];
      const footer = [...this.panelDef.meta.footer];
      const fields = this.panelDefFields.map((row) => [...row]);

      moveItemInArray(columns, event.previousIndex, event.currentIndex);
      moveItemInArray(footer, event.previousIndex, event.currentIndex);

      // Update fields
      fields.forEach((row) => {
        moveItemInArray(row, event.previousIndex, event.currentIndex);
      });

      this.panelDef = {
        ...this.panelDef,
        meta: {
          ...this.panelDef.meta,
          columns,
          fields,
          footer,
        },
      };

      this.updateColumnControls();
    }
  }

  dropRows(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const fields = [...this.panelDefFields];
    moveItemInArray(fields, event.previousIndex, event.currentIndex);

    this.panelDef = {
      ...this.panelDef,
      meta: {
        ...this.panelDef.meta,
        fields,
      },
    };

    this.updateRowControls();
  }

  private setDynamicColDefs(): void {
    this.columnDefs = this.panelDefColumns.map((column, columnIndex) => ({
      name: `column_${columnIndex}`,
      label: columnIndex === 0 ? '' : column || `Column ${columnIndex + 1}`,
      type: (params) => {
        return params?.node?.rowPinned ? ('text' as FieldDefType) : this.lookupFieldByLabel(column)?.type;
      },
      tsTableOptions: {
        field: `column_${columnIndex}`,
        headerName: columnIndex === 0 ? '' : column,
        sortable: columnIndex !== 0,
        minWidth: 100,
        wrapText: true,
        resizable: true,
        suppressMovable: true,
        editable: (params) => {
          const field = this.getField(params);
          const isNestedField = field?.name.includes('.');
          return (
            field &&
            columnIndex !== 0 &&
            !isNestedField &&
            !field?.meta?.readonly &&
            !field.locked &&
            !field?.meta?.value_hidden &&
            field.name !== this.placeholderText
          );
        },
        cellRendererSelector: (params) => {
          if (params?.node?.rowPinned) {
            return {
              component: TableFooterAggRendererComponent,
            };
          }
          const field = this.getField(params);
          return this.tsTableService.setCellRendererSelector(field, this.relatedToRedirectConfig, this.data);
        },
        cellEditorSelector: (params) => {
          let field = this.getField(params);
          if (field?.meta?.record_type === RelatedToType.User) {
            field = {
              meta: {
                options: this.store
                  //@ts-ignore
                  .selectSnapshot(FieldDefsState.selectDropdownOptionsBy('users'))
                  .map((x) => ({ id: x.id, label: x.name })),
              },
              ...field,
            };
          }
          return this.tsTableService.setCellEditorSelector(field, this.relatedToRedirectConfig, this.data);
        },
        valueFormatter: (params) => {
          const fieldDef = this.getField(params);
          return this.tsTableService.setValueFormatterSelector(
            fieldDef,
            this.relatedToRedirectConfig,
            this.data,
          )(params);
        },
      },
    }));
  }

  private setLayoutFields(): void {
    this.layoutFieldDefs.next(
      this.fieldDefs.filter((x) =>
        this.panelDef.meta.fields
          .reduce((p, c) => p.concat(c.filter((x, i) => i > 0).filter((x) => x != this.placeholderText)), [])
          .includes(x.name),
      ),
    );
  }

  private getFieldDisplayValue(fieldName: string, columnIndex: number): any {
    if (!fieldName || fieldName === this.placeholderText) {
      return '';
    }

    const fieldDef = this.lookupField(fieldName, true);
    return fieldDef ? this.model?.[fieldName] : columnIndex === 0 ? fieldName : null;
  }

  private setRowData(): void {
    this.rowData = this.panelDefFields.map((row, rowIndex) => {
      const rowData = row.reduce((data, cellName, columnIndex) => {
        data[`column_${columnIndex}`] = this.getFieldDisplayValue(cellName, columnIndex);
        return data;
      }, {});

      rowData['rowIndex'] = rowIndex;
      return rowData;
    });
  }

  private getPinnedRowData() {
    if (!this.columnDefs || this.columnDefs.length === 0) {
      return [];
    }

    return [
      this.columnDefs.reduce((acc, col, index) => {
        acc[`column_${index}`] = this.getAggValue(index);
        return acc;
      }, {}),
    ];
  }

  private checkTable(): void {
    this.newTable.set(this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_TABLE)));
  }

  dropTable(event: CdkDragDrop<any[]>) {
    if (event.item.data === 'row') {
      this.dropRows(event);
    } else {
      this.dropColumns(event);
    }
    this.table.renderRows();
  }

  isColumnDraggable(columnIndex: number): boolean {
    return columnIndex > 0 && this.panelDefColumns[columnIndex] !== '[column]';
  }

  saveLayout(): void {
    this.panelDef = {
      ...this.panelDef,
      color: this.panelColor,
      title: this.title.value,
      meta: {
        ...this.panelDef.meta,
        columns: this.panelDefColumns?.map((column, index) => this.columnControls[index].value || `Column${index}`),
        fields: this.panelDefFields
          .filter((row) => row.length)
          .map((row, index) => {
            row[0] = this.rowControls[index].value || `Row${index + 1}`;
            return row;
          }),
      },
    };

    this.panelDefUpdated.emit(this.panelDef);
    this.ui = { ...this.ui, editMode: !this.ui.editMode };
    this.editToggled.emit({ toggle: false, panelDef: this.panelDef });

    this.setLayoutFields();
  }

  lookupField(name: string, ignorePlaceholder = false): FieldDef {
    if (!ignorePlaceholder && this.placeholderField.name === name) {
      return null;
    }

    return this.fieldDefs.find((fieldDef) => fieldDef.name === name);
  }

  lookupFieldByLabel(label: string): FieldDef {
    return this.fieldDefs.find((fieldDef) => fieldDef.label === label);
  }

  update(data: any): void {
    this.pendingChanges.set({ ...this.pendingChanges(), ...data });
  }

  updateRowName(rowIndex: number, columnIndex: number): void {
    const value = this.rowControls[rowIndex].value;
    this.panelDefFields[rowIndex][columnIndex] = value;
  }

  updateColumnName(columnIndex: number): void {
    const value = this.columnControls[columnIndex].value;
    this.panelDefColumns[columnIndex] = value;
  }

  saveChanges(): void {
    if (this.router.url.startsWith('/space')) {
      // this is temporary
      this.dataUpdated.emit({ model: { id: this.model.id, ...this.pendingChanges() }, origin: this.panelDef });
    } else {
      this.dataUpdated.emit(this.pendingChanges());
    }

    this.savingChanges.set(true);
    this.modelMemento = JSON.parse(JSON.stringify(this.model));
  }

  discardChanges(): void {
    this.model = JSON.parse(JSON.stringify(this.modelMemento));
    this.pendingChanges.set({});
    this.setRowData();
    this.refreshFooter();
  }

  completeSaving(): void {
    this.pendingChanges.set({});
    this.savingChanges.set(false);
  }

  manageColumn(mode: ManageMode, index?: number): void {
    if (mode === 'add') {
      const columnLabel = `Column${this.panelDefColumns?.length}`;

      this.columnControls[this.panelDefColumns?.length] = new FormControl(columnLabel);

      this.panelDef = {
        ...this.panelDef,
        meta: {
          ...this.panelDef.meta,
          columns: [...this.panelDefColumns, columnLabel],
          fields: this.panelDefFields.map((item) => [...item, this.placeholderField.name]),
          footer: [...this.panelDef.meta.footer, 'sum'],
        },
      };
    } else {
      this.panelDefColumns?.splice(index, 1);
      this.panelDef.meta.footer.splice(index, 1);

      this.updateColumnControls();

      this.panelDef = {
        ...this.panelDef,
        meta: {
          ...this.panelDef.meta,
          fields: this.panelDefFields.map((items) => {
            items.splice(index, 1);
            return items;
          }),
        },
      };
    }
  }

  manageRow(mode: ManageMode, index?: number): void {
    if (mode === 'add') {
      const rowLabel = `Row${this.panelDefFieldsLength + 1}`;

      this.rowControls[this.panelDefFieldsLength] = new FormControl(rowLabel);

      this.panelDef = {
        ...this.panelDef,
        meta: {
          ...this.panelDef.meta,
          fields: [
            ...this.panelDefFields,
            Array.from(Array(this.panelDefColumnsLength).keys()).map((x, i) =>
              i === 0 ? rowLabel : this.placeholderField.name,
            ),
          ],
        },
      };
    } else {
      this.panelDef = {
        ...this.panelDef,
        meta: {
          ...this.panelDef.meta,
          fields: this.panelDefFields.filter((items, i) => i !== index),
        },
      };

      this.updateRowControls();
    }
  }

  getAggValue(columnIndex: number): string {
    let eligibleFieldsCount = 0;
    const sum = this.panelDefFields.reduce((prev, curr) => {
      const fieldName = curr[columnIndex];

      if (fieldName === this.placeholderText) {
        return prev;
      }

      const fieldDef = this.fieldDefs.find(({ name }) => name === fieldName);

      if (!this.aggValidTypes.includes(fieldDef?.type)) {
        return prev;
      }

      if (this.model[fieldName] === null || this.model[fieldName] === undefined) {
        return prev;
      }

      eligibleFieldsCount += 1;

      return prev + Number(this.model[fieldName]);
    }, 0);

    if (this.panelDef.meta.footer[columnIndex] === 'avg') {
      if (sum === 0 && eligibleFieldsCount === 0) return '0';

      return new RemoveZeroDecimalsPipe().transform(
        new FormatNumberPipe().transform(String(sum / eligibleFieldsCount)),
      );
    }

    return new RemoveZeroDecimalsPipe().transform(new FormatNumberPipe().transform(String(sum)));
  }

  toggleAgg(columnIndex: number, type: SumOrAvg = 'sum'): void {
    if (this.panelDef.meta.footer[columnIndex] === type) {
      return;
    }

    this.panelDef.meta.footer[columnIndex] = type;

    this.silentPanelDefUpdated.emit(this.panelDef);

    this.refreshFooter();
  }

  toggleFooter(): void {
    this.panelDef.meta.showFooter = !this.panelDef.meta.showFooter;
    this.silentPanelDefUpdated.emit(this.panelDef);

    this.refreshFooter();
  }

  hasEditPermission(): [NewPermission, boolean] | boolean {
    const editPermissionsMap = {
      [RelatedToType.Project]: NewPermission.DEALS_EDIT,
      [RelatedToType.Property]: NewPermission.PROPERTIES_EDIT,
    };

    if (editPermissionsMap[this.relatedToType]) {
      return [editPermissionsMap[this.relatedToType], this.model.permissions?.is_editable];
    }

    return true;
  }

  hasSharingPermission(): [NewPermission, boolean] | boolean {
    if (this.relatedToType === RelatedToType.Project) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.DEALS_SHARING_RECORDS));
    } else if (this.relatedToType === RelatedToType.Property) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.PROPERTIES_SHARING_RECORDS));
    }
    return true;
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    if (this.gridApi) {
      const pinnedRowData = this.panelDef?.meta?.showFooter ? this.getPinnedRowData() : [];
      this.gridApi.setGridOption('pinnedBottomRowData', pinnedRowData);

      this.gridApi.addEventListener('cellEditingStopped', (event) => {
        const fieldName = this.getField(event);
        const value = event.newValue;
        if (fieldName) {
          const rowIndex = event.node.rowIndex;
          // @ts-ignore
          const columnIndex = event.column.colId.split('_')[1];
          this.panelDefFields[rowIndex][columnIndex] = fieldName.name;

          this.model[fieldName.name] = value;

          const formattedData = {
            [fieldName.name]: value,
          };
          this.update(formattedData);
          const updatedPinnedRowData = this.panelDef?.meta?.showFooter ? this.getPinnedRowData() : [];
          this.gridApi.setGridOption('pinnedBottomRowData', updatedPinnedRowData);
        }
      });
    }
  }

  exportMatrix(): void {
    const dialogRef = this.dialog.open(MatrixExportDialogComponent, {
      width: '600px',
    });

    let fileType = '';

    dialogRef
      .afterClosed()
      .pipe(
        switchMap((result: MatrixExportDialogResult) => {
          if (!result) {
            this.snackBar.open('No file type selected', 'Close', {
              duration: 2000,
            });
            return EMPTY;
          }
          fileType = result;
          const panelId = this.panelDef.id;
          const relatedToType = this.relatedToType;

          if (relatedToType === RelatedToType.Project) {
            const relatedToId = this?.data?.project?.id;
            return this._projectsService.projectTemplate$.pipe(
              switchMap((template) =>
                this._projectsService.getMatrixForExport(
                  panelId,
                  relatedToType,
                  relatedToId,
                  'ProjectTemplate',
                  template.id.toString(),
                  result,
                ),
              ),
            );
          } else if (relatedToType === RelatedToType.Property) {
            const relatedToId = this?.data?.property?.id;
            return this._propertiesService.property$.pipe(
              switchMap((property) =>
                this._projectsService.getMatrixForExport(
                  panelId,
                  relatedToType,
                  relatedToId,
                  'ProjectTemplate',
                  property.template.id.toString(),
                  result,
                ),
              ),
            );
          } else {
            return EMPTY;
          }
        }),
      )
      .subscribe((response) => {
        if (response) {
          const url = window.URL.createObjectURL(response);
          const a = document.createElement('a');
          a.href = url;
          a.download = `table.${fileType}`;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      });
  }

  historyOpen = signal(false);

  onCloseByBackdropClick(): void {
    this.historyOpen.set(false);
  }

  onOpened(): void {
    this.historyOpen.set(true);
  }

  private refreshFooter(): void {
    if (this.gridApi) {
      const pinnedRowData = this.panelDef.meta.showFooter ? this.getPinnedRowData() : [];
      this.gridApi.setGridOption('pinnedBottomRowData', pinnedRowData);
    }
  }
}
